import { useEffect, useState } from "react";
import GuestsInput from "../GuestsInput";
import LocationSearchInput from "components/GoogleMaps/LocationSearchInput";
import DatesRangeInput from "../DatesRangeInput";
import convertSelectedDateToString from "utils/convertSelectedDateToString";
import { useSelector } from "react-redux";
import { 
  selectMood, 
  setMood,
  selectSearchType,
  setSearchType,
  selectCheckIn,
  selectCheckOut,
  selectGuests,
} from "application/features/search/searchSlice";
import Select from "shared/Select/Select";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { StockEnum } from "data/enums";

const StaySearchForm = () => {

  const { t, ready } = useTranslation("translation");
  const [
    fieldNameShow, 
    setFieldNameShow
  ] = useState<"location" | "dates" | "guests" | "mood" | "typeSelector">("location");

  const [locationInputTo, setLocationInputTo] = useState("");

  //
  const dispatch = useDispatch();

  // Get values from the global state
  const mood: string = useSelector(selectMood);
  const searchType = useSelector(selectSearchType);
  const startDateFromSearch = useSelector(selectCheckIn);
  const endDateFromSearch = useSelector(selectCheckOut);
  const guestsFromSearch = useSelector(selectGuests);

  useEffect(() => {
    if (!searchType) {
      dispatch(setSearchType(StockEnum.STAY));
    }
    if (!mood) {
      // reset the mood to "any" when the component mounts
      dispatch(setMood(""));
    }
  }, [])

  if (!ready) return null;

  const bookingTypes: { [key: string]: string } = t("translation:search.common.bookingTypes", { returnObjects: true });
  const moodSelection: { [key: string]: { label: string; description: string } } = t("translation:components.moods", { returnObjects: true });

  const renderInputLocation = () => {
    const isActive = fieldNameShow === "location";
    return (
      <div
        className={`w-full bg-white dark:bg-neutral-800 ${isActive
          ? "rounded-2xl shadow-lg"
          : "rounded-xl shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]"
          }`}
      >
        {!isActive ? (
          <button
            className={`w-full flex justify-between text-sm font-medium p-4`}
            onClick={() => setFieldNameShow("location")}
          >
            <span className="text-neutral-400">{t("translation:search.common.where")}</span>
            <span>{locationInputTo || "Location"}</span>
          </button>
        ) : (
          <LocationSearchInput
            defaultValue={locationInputTo}
            onChange={(value) => {
              setLocationInputTo(value);
              setFieldNameShow("dates");
            }}
            headingText={t("translation:search.common.destination")}
          />
        )}
      </div>
    );
  };

  const renderInputDates = () => {
    const isActive = fieldNameShow === "dates";

    return (
      <div
        className={`w-full bg-white dark:bg-neutral-800 overflow-hidden ${isActive
          ? "rounded-2xl shadow-lg"
          : "rounded-xl shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]"
          }`}
      >
        {!isActive ? (
          <button
            className={`w-full flex justify-between text-sm font-medium p-4  `}
            onClick={() => setFieldNameShow("dates")}
          >
            <span className="text-neutral-400">{t("translation:search.common.when")}</span>
            <span>
              {(startDateFromSearch && endDateFromSearch)
                ? convertSelectedDateToString([startDateFromSearch, endDateFromSearch])
                : t("translation:search.common.addDate")}
            </span>
          </button>
        ) : (
          <DatesRangeInput onChange={(dates) => {
            if (dates[0] && dates[1]) {
              setFieldNameShow("guests");
            }
          }}/>
        )}
      </div>
    );
  };

  const renderInputGuests = () => {
    const isActive = fieldNameShow === "guests";

    return (
      <div
        className={`w-full bg-white dark:bg-neutral-800 overflow-hidden ${isActive
          ? "rounded-2xl shadow-lg"
          : "rounded-xl shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]"
          }`}
      >
        {!isActive ? (
          <button
            className={`w-full flex justify-between text-sm font-medium p-4`}
            onClick={() => setFieldNameShow("guests")}
          >
            <span className="text-neutral-400">{t("translation:search.common.who")}</span>
            <span>{guestsFromSearch || t("translation:search.common.addGuests")}</span>
          </button>
        ) : (
          <GuestsInput />
        )}
      </div>
    );
  };

  const handleShowMoodDescription = () => {
    switch (mood) {
      case moodSelection.comfort.label:
        return <span className="mb-5 block  text-md sm:text-md">
          {moodSelection.comfort.description}
        </span>
      case moodSelection.business.label:
        return <span className="mb-5 block  text-md sm:text-md">
          {moodSelection.business.description}
        </span>
      case moodSelection.authentic.label:
        return <span className="mb-5 block  text-md sm:text-md">
          {moodSelection.authentic.description}
        </span>
      default:
        return null
    }
  }

  const renderInputMood = () => {
    const isActive = fieldNameShow === "mood";

    return (
      <div
        className={`w-full bg-white dark:bg-neutral-800 overflow-hidden ${isActive
          ? "rounded-2xl shadow-lg"
          : "rounded-xl shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]"
          }`}
      >
        {!isActive ? (
          <button
            className={`w-full flex justify-between text-sm font-medium p-4`}
            onClick={() => setFieldNameShow("mood")}
          >
            <span className="text-neutral-400">{t("translation:search.common.mood")}</span>
            <span>
              {
                mood ? (
                  mood.charAt(0).toLocaleUpperCase() + mood.slice(1).toLocaleLowerCase()
                ) : t("translation:search.common.any")
              }
            </span>
          </button>
        ) : (
          <div className={`flex flex-col relative p-5`}>
            <span className="mb-5 block font-semibold text-xl sm:text-2xl">
              {`Mood`}
            </span>
            {
              handleShowMoodDescription()
            }
            <Select
              onChange={(e) => dispatch(setMood(e.target.value))}
              defaultValue={mood}
            >
              <option value="">{t("translation:search.common.any")}</option>
              <option value="comfort">{moodSelection.comfort.label}</option>
              <option value="business">{moodSelection.business.label}</option>
              <option value="authentic">{moodSelection.authentic.label}</option>
            </Select>
          </div>
        )}
      </div>
    );
  };

  const renderTypeInput = () => {
    const isActive = fieldNameShow === "typeSelector";

    return (
      <div
        className={`w-full bg-white dark:bg-neutral-800 overflow-hidden ${isActive
          ? "rounded-2xl shadow-lg"
          : "rounded-xl shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]"
          }`}
      >
        {!isActive ? (
          <button
            className={`w-full flex justify-between text-sm font-medium p-4`}
            onClick={() => setFieldNameShow("typeSelector")}
          >
            <span className="text-neutral-400">{t("translation:search.common.searchType")}</span>
            <span>{bookingTypes[searchType]}</span>
          </button>
        ) : (
          <div className={`flex flex-col relative p-5`}>
            <span className="mb-5 block font-semibold text-xl sm:text-2xl">
              {t("translation:search.common.searchType")}
            </span>

            <Select
              onChange={(e) => {
                dispatch(setSearchType(e.target.value));
              }}
              defaultValue={searchType}
            >
              {
                Object.entries(bookingTypes).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))
              }
            </Select>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="w-full space-y-5">
        {/*  */}
        {renderInputLocation()}
        {/*  */}
        {renderInputDates()}
        {/*  */}
        {renderInputGuests()}
        {/*  */}
        {renderTypeInput()}
        {/*  */}
        {renderInputMood()}

      </div>
    </div>
  );
};

export default StaySearchForm;
